import http from '../httpService';

// ── Settings DTOs ─────────────────────────────────────────────────────────────

export interface TrustedDeviceDto {
  id: string;
  deviceLabel: string;
  createdAt: string;
  expiresAt: string;
}

export interface TwoFactorStatusDto {
  isEnabled: boolean;
  method: string | null;
  /** The method (Email/Sms) that will be used when the user enables 2FA. */
  resolvedMethod: string;
  phoneNumber: string | null;
  isPhoneConfirmed: boolean;
  trustedDevices: TrustedDeviceDto[];
}

export interface SendTwoFactorCodeInput {
  // intentionally empty — method is resolved server-side from confirmed phone/email
}

export interface SendPhoneVerificationCodeInput {
  /** Full international E164 format, e.g. +447911123456. */
  phoneNumber: string;
}

export interface VerifyPhoneInput {
  challengeToken: string;
  code: string;
}

export interface VerifyTwoFactorEnableInput {
  challengeToken: string;
  code: string;
}

export interface RemoveTrustedDeviceInput {
  deviceId: string;
}

export interface TotpSetupDto {
  setupToken: string;
  qrCodeUri: string;
  manualEntryKey: string;
}

// ── Login DTOs ────────────────────────────────────────────────────────────────

export interface VerifyTwoFactorLoginInput {
  challengeToken: string;
  code: string;
  rememberDevice: boolean;
  deviceLabel?: string;
}

export interface VerifyTwoFactorLoginResult {
  accessToken: string;
  encryptedAccessToken: string;
  expireInSeconds: number;
  refreshToken: string;
  refreshTokenExpireInSeconds: number;
  userId: number;
  tenantId: number | null;
  /** Returned only when rememberDevice is true. Store in localStorage. */
  trustToken?: string;
}

class TwoFactorAuthService {
  // ── Settings methods ───────────────────────────────────────────────────────

  public async getStatus(): Promise<TwoFactorStatusDto> {
    const result = await http.get('api/services/app/TwoFactorAuth/GetStatus');
    return result.data.result;
  }

  public async sendEnableVerificationCode(
    input: SendTwoFactorCodeInput
  ): Promise<{ challengeToken: string }> {
    const result = await http.post(
      'api/services/app/TwoFactorAuth/SendEnableVerificationCode',
      input
    );
    return result.data.result;
  }

  public async verifyAndEnable(input: VerifyTwoFactorEnableInput): Promise<void> {
    await http.post('api/services/app/TwoFactorAuth/VerifyAndEnable', input);
  }

  public async sendDisableVerificationCode(): Promise<{ challengeToken: string }> {
    const result = await http.post(
      'api/services/app/TwoFactorAuth/SendDisableVerificationCode',
      {}
    );
    return result.data.result;
  }

  public async verifyAndDisable(input: VerifyTwoFactorEnableInput): Promise<void> {
    await http.post('api/services/app/TwoFactorAuth/VerifyAndDisable', input);
  }

  public async removeTrustedDevice(input: RemoveTrustedDeviceInput): Promise<void> {
    await http.delete('api/services/app/TwoFactorAuth/RemoveTrustedDevice', { params: input });
  }

  public async getTotpSetup(): Promise<TotpSetupDto> {
    const result = await http.post('api/services/app/TwoFactorAuth/GenerateTotpSetup', {});
    return result.data.result;
  }

  public async verifyAndEnableTotp(input: { setupToken: string; code: string }): Promise<void> {
    await http.post('api/services/app/TwoFactorAuth/VerifyAndEnableTotp', input);
  }

  public async sendPhoneVerificationCode(
    input: SendPhoneVerificationCodeInput
  ): Promise<{ challengeToken: string }> {
    const result = await http.post(
      'api/services/app/TwoFactorAuth/SendPhoneVerificationCode',
      input
    );
    return result.data.result;
  }

  public async verifyPhone(input: VerifyPhoneInput): Promise<void> {
    await http.post('api/services/app/TwoFactorAuth/VerifyPhone', input);
  }

  // ── Login verification ────────────────────────────────────────────────────

  public async verifyLoginCode(
    input: VerifyTwoFactorLoginInput
  ): Promise<VerifyTwoFactorLoginResult> {
    const result = await http.post('api/TokenAuth/VerifyTwoFactorLogin', input);
    return result.data.result;
  }
}

export default new TwoFactorAuthService();
